import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/** 계정 관리 — 백엔드 AccountAdminController(DOC-141 §3 · 메뉴 키 safe_accounts). */

export type AccountKind = 'VENDOR' | 'STAFF';
export type AccountStatus = 'INVITED' | 'ACTIVE' | 'DISABLED';

export type Ref = { id: string; name: string };
export type Option = { id: string; name: string; note: string | null };

export type Account = {
  id: string;
  kind: AccountKind;
  name: string;
  email: string;
  phone: string | null;
  status: AccountStatus;
  vendorId: string | null;
  vendorName: string | null;
  personnelId: string | null;
  hasPassword: boolean;
  sites: Ref[];
  roles: Ref[];
};

export type AccountOptions = { vendors: Option[]; personnel: Option[]; sites: Option[]; roles: Option[] };

export type AccountInput = {
  kind: AccountKind;
  vendorId: string | null;
  personnelId: string | null;
  name: string;
  email: string;
  phone: string;
  password: string;
  siteIds: string[];
  roleIds: string[];
};

export const STATUS_LABEL: Record<AccountStatus, { label: string; tone: string }> = {
  ACTIVE: { label: '사용', tone: 'badge-green' },
  INVITED: { label: '비밀번호 전', tone: 'badge-yellow' },
  DISABLED: { label: '꺼짐', tone: 'badge-gray' },
};

const BASE = '/v1/safe/accounts';
const KEY = ['safe-accounts'];

export function useAccounts() {
  return useQuery({ queryKey: KEY, queryFn: () => apiFetch<Account[]>(BASE) });
}

export function useAccountOptions() {
  return useQuery({ queryKey: [...KEY, 'options'], queryFn: () => apiFetch<AccountOptions>(`${BASE}/options`), staleTime: 60_000 });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/** 새 계정은 POST, 있는 계정은 PUT(종류 · 직원 · 비밀번호는 안 바뀐다). */
export function useSaveAccount() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: AccountInput }): Promise<unknown> =>
      id
        ? apiFetch<void>(`${BASE}/${id}`, {
            method: 'PUT',
            body: { vendorId: input.vendorId, name: input.name, email: input.email, phone: input.phone, siteIds: input.siteIds, roleIds: input.roleIds },
          })
        : apiFetch<string>(BASE, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useAccountStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => apiFetch<void>(`${BASE}/${id}/status`, { method: 'PUT', body: { active } }),
    onSuccess: invalidate,
  });
}

export function useAccountPassword() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => apiFetch<void>(`${BASE}/${id}/password`, { method: 'PUT', body: { password } }),
    onSuccess: invalidate,
  });
}
